import { DestroyRef, Injectable, NgZone, Signal, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Non-production helper that flips a global "debug layout" signal when
 * the user presses Ctrl+Alt+D anywhere in the app.
 *
 * Why: XBRL sheets like role19 can render ~26 columns x 15+ rows, which is
 * painful to scan for validation errors. Sheets that want an alternate,
 * developer-friendly layout subscribe to `effectiveEnabled` and render the
 * refactored view while this signal is `true`.
 *
 * ─── Transition state ─────────────────────────────────────────────────
 *
 * Swapping the two layouts destroys / instantiates thousands of
 * `<p-inputnumber>` components synchronously, which freezes the main
 * thread for ~2 seconds on Role 19. To give the user feedback during
 * that freeze we publish two signals:
 *
 *   - `transitioning` — true between the keypress and the applied flip;
 *     templates render a spinner while this is true.
 *   - `effectiveEnabled` — what templates render against. Only flips
 *     AFTER the browser has had a chance to paint the spinner.
 *
 * The keypress schedules the flip through a double-`requestAnimationFrame`
 * (first fires before paint, second fires after paint), wrapped in
 * `runOutsideAngular` so Zone.js doesn't spam change detection during
 * the paint window. Signal writes re-enter the zone so consumers
 * naturally re-render.
 *
 * `enabled` is preserved as a read-only alias of the "intended" state
 * for callers (including docstrings) that only need the boolean.
 *
 * The listener is never attached in production builds — the
 * `environment.production` check is a compile-time constant so the body
 * is dead-code eliminated by the Angular optimiser.
 */
@Injectable({ providedIn: 'root' })
export class DebugLayoutToggleService {
  private readonly _enabled = signal<boolean>(false);
  private readonly _effectiveEnabled = signal<boolean>(false);
  private readonly _transitioning = signal<boolean>(false);

  /** Intended toggle state (flips synchronously on keypress). */
  readonly enabled: Signal<boolean> = this._enabled.asReadonly();
  /** Applied toggle state — what templates should render against. */
  readonly effectiveEnabled: Signal<boolean> = this._effectiveEnabled.asReadonly();
  /** True while the layout is swapping; show a spinner. */
  readonly transitioning: Signal<boolean> = this._transitioning.asReadonly();

  private readonly zone = inject(NgZone);
  private readonly handler = (event: KeyboardEvent) => this.onKey(event);
  private refCount = 0;

  bindGlobalShortcut(destroyRef: DestroyRef): void {
    if (environment.production) return;

    if (this.refCount === 0) {
      window.addEventListener('keydown', this.handler);
    }
    this.refCount++;

    destroyRef.onDestroy(() => {
      this.refCount = Math.max(0, this.refCount - 1);
      if (this.refCount === 0) {
        window.removeEventListener('keydown', this.handler);
      }
    });
  }

  private onKey(event: KeyboardEvent): void {
    if (!event.ctrlKey || !event.altKey || event.shiftKey || event.metaKey) return;
    if (event.code !== 'KeyD') return;
    event.preventDefault();
    if (this._transitioning()) return;

    this._enabled.update((isEnabled) => !isEnabled);
    this._transitioning.set(true);

    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.zone.run(() => {
            this._effectiveEnabled.set(this._enabled());
            this._transitioning.set(false);
          });
        });
      });
    });
  }
}
